import React from 'react'
import { useTranslation } from 'react-i18next'
import { groupBy } from 'lodash'
import { useReactiveVar } from '@apollo/client'
import { PrimaryEnergyFlow } from '../../../calculators/EnergyFlow'
import {
  energyPerAreaUnit,
  energyPerYearUnit,
  powerPerAreaUnit,
  powerUnit,
} from '../../../util/formatters'
import { getFilteredResultsFromDemands } from '../../../calculators/calculator-result-utility'
import { ResultSubCategory } from '../../../provider/result-categories'
import { mapNamedResultsToRows } from './report-utility'
import { ReportTable } from './ReportTable'
import { dictionariesVar } from '../../../apollo/reactive-vars'

type Props = {
  demandFlows: PrimaryEnergyFlow[]
  subCategory: ResultSubCategory
  area?: number
}

export function ReportDemandsTable({
  demandFlows,
  subCategory,
  area,
}: Props): React.ReactElement {
  const { t } = useTranslation()
  const { demandIdsToNames } = useReactiveVar(dictionariesVar)

  const filteredDemands = demandFlows
    .filter((demand) => demand.energyType === subCategory.energyType)
    .map((flow) => ({
      ...flow,
      name: demandIdsToNames[flow.demandId],
    }))
  const demandsByName = groupBy(filteredDemands, (demand) => demand.name)
  const resultsByName = Object.entries(demandsByName).map(([name, flows]) => ({
    name,
    result: getFilteredResultsFromDemands(flows, subCategory),
  }))

  const rows = mapNamedResultsToRows(resultsByName, {
    area,
    totalRowName: t('common.total'),
    maxRowName: t('common.max'),
  })

  const energyHeaderCells = [
    { firstRow: t('unitName.totalEnergy'), secondRow: energyPerYearUnit },
  ]
  const powerHeaderCells = [
    { firstRow: t('unitName.maxPower'), secondRow: powerUnit },
  ]

  if (area) {
    energyHeaderCells.push({
      firstRow: t('unitName.totalEnergyPerArea'),
      secondRow: energyPerAreaUnit,
    })
    powerHeaderCells.push({
      firstRow: t('unitName.maxPowerPerArea'),
      secondRow: powerPerAreaUnit,
    })
  }

  const headerCells = [...energyHeaderCells, ...powerHeaderCells]

  return (
    <ReportTable
      title={t(subCategory.nameKey)}
      headerCells={headerCells}
      rows={rows}
    />
  )
}
